<template>
  <div v-if="contentRef" class="md:sticky self-start top-40">
    <div class="mb-8 py-4 pl-2 lg:pl-4 text-sm" :class="backgroundClass">
      <h6 v-if="showHeader" class="font-bold text-space-cadet ml-6 mb-3">
        {{ $t("block.tableContents") }}
      </h6>
      <ul class="section-link text-secondary">
        <li
          v-for="header in headers"
          :key="header.offsetTop"
          :class="
            cn(
              'flex mb-3 pr-4 text-left items-center text-secondary',
              props.anchorClass,
              header === activeHeader && 'text-primary'.concat(' ', props.activeAnchorClass)
            )
          "
        >
          <div class="min-w-[20px]">
            <NuxtImg
              v-show="header === activeHeader"
              src="/icons/arrow-right.svg"
              loading="lazy"
              alt=""
              width="12"
              height="10"
            />
          </div>
          <a
            class="transition-all ease-in-out ml-1 cursor-pointer hover:underline text-inherit"
            @click="scrollToTarget(header.offsetTop)"
          >
            {{ header.textContent || (header.id === "intro" ? "Introduction" : "Untitled Section") }}
          </a>
        </li>
      </ul>
    </div>
    <slot></slot>
  </div>
</template>

<script setup lang="ts">
interface Props {
  contentRef: HTMLElement | null;
  autoFocusFirst?: boolean;
  headerTag?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  transparentBackground?: boolean;
  showHeader?: boolean;
  anchorClass?: string;
  activeAnchorClass?: string;
}

const props = withDefaults(defineProps<Props>(), {
  autoFocusFirst: false,
  headerTag: "h2",
  transparentBackground: false,
  showHeader: true,
  anchorClass: "",
  activeAnchorClass: "",
});

const autoFocusFirstSet = ref(false);
const activeHeader = ref<HTMLHeadingElement | null>(null);
const contentRefProp = computed(() => props.contentRef);
const headers = ref<HTMLHeadingElement[]>([]);
const headerHeight = 115;

const backgroundClass = computed(() => {
  return props.transparentBackground ? "bg-transparent" : "bg-stone-white";
});

watch(contentRefProp, mountTOC);

function mountTOC(newContentRefProp: HTMLElement | null) {
  if (newContentRefProp instanceof HTMLElement) {
    headers.value = Array.from(newContentRefProp.querySelectorAll(props.headerTag));
  }

  if (props.autoFocusFirst && !autoFocusFirstSet.value) {
    autoFocusFirstSet.value = true;
    activeHeader.value = headers.value[0];
  }
}

function scrollToTarget(offsetTop: number) {
  window.scrollTo({
    top: offsetTop - headerHeight + 1,
    behavior: "smooth",
  });
}

function addScrollEventListener() {
  window.addEventListener("scroll", () => {
    headers.value?.forEach((header) => {
      if (header.offsetTop - headerHeight <= window.scrollY) {
        activeHeader.value = header;
      }
    });
  });
}

onMounted(() => {
  addScrollEventListener();
});
</script>
